import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Building2,
  CalendarClock,
  CalendarX,
  Camera,
  ClipboardList,
  Clock,
  CreditCard,
  FilePen,
  FileSignature,
  MailWarning,
  Trophy,
  Music,
  TriangleAlert,
  UserMinus,
} from "lucide-react";
import { formatDate, pluralizeCzech } from "@marekl/adminer/format";
import type { DashboardAlert, AlertSeverity } from "../../../lib/queries/dashboard";

type AlertVariant = "info" | "warn" | "err";

export function severityToVariant(sev: AlertSeverity): AlertVariant {
  return sev;
}

// Severity decides the reading order, not the order the service happened to
// append in. Without this an „info" (chybí fotka) can sit above an „err"
// (žádná aktivní sezóna), which is the wrong thing to read first.
const SEVERITY_RANK: Record<AlertSeverity, number> = { err: 0, warn: 1, info: 2 };

export function sortAlerts(alerts: DashboardAlert[]): DashboardAlert[] {
  return [...alerts].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

function str(data: Record<string, unknown>, key: string): string {
  const v = data[key];
  return v === undefined || v === null ? "" : String(v);
}

function num(data: Record<string, unknown>, key: string): number {
  const v = data[key];
  return typeof v === "number" ? v : Number(v ?? 0);
}

const teams = (n: number) => pluralizeCzech(n, ["tým", "týmy", "týmů"]);
const members = (n: number) => pluralizeCzech(n, ["člena", "členů", "členů"]);
const days = (n: number) => pluralizeCzech(n, ["den", "dny", "dní"]);
const entriesWord = (n: number) => pluralizeCzech(n, ["přihlášku", "přihlášky", "přihlášek"]);

/** „zbývá 1 den" / „zbývají 3 dny" / „zbývá 7 dní" / „uzávěrka je dnes". */
function leftIn(n: number): string {
  if (n <= 0) return "uzávěrka je dnes";
  return `${pluralizeCzech(n, ["zbývá", "zbývají", "zbývá"])} ${n} ${days(n)}`;
}

export interface AlertCta {
  label: string;
  to: string;
}

export interface AlertContent {
  /** Accompanies the text, never replaces it — DESIGN.md §3. */
  icon: LucideIcon;
  /** The strong headline. */
  title: string;
  /** The detail sentence. */
  body: string;
  /** The quieter „and this is why it matters" line. */
  note?: string;
  cta?: AlertCta;
}

// alertContent maps a machine-readable alert to its Czech copy, icon and CTA.
// The backend deliberately ships no user-facing strings, so this file is the
// only place the wording exists.
//
// Adding a kind in Go without adding a case here renders a blank „Upozornění"
// with no type error and no failing test — add the union member in
// lib/queries/dashboard.ts and a case here in the same change.
export function alertContent(a: DashboardAlert): AlertContent {
  const d = a.data;
  switch (a.kind) {
    case "no_active_season":
      return {
        icon: CalendarX,
        title: "Není nastavena aktivní sezóna",
        body: "Bez aktivní sezóny nelze evidovat členství ani registrace.",
        cta: { label: "Spravovat sezóny", to: "/admin/sezony" },
      };
    case "mail_misconfigured": {
      const n = num(d, "count");
      return {
        icon: MailWarning,
        title: "E-maily se neodesílají",
        body:
          n > 0
            ? `Služba pro odesílání e-mailů odmítá poštu kvůli chybnému nastavení (klíč, odesílatel nebo adresa služby). ${pluralizeCzech(n, ["Čeká", "Čekají", "Čeká"])} ${n} ${pluralizeCzech(n, ["e-mail", "e-maily", "e-mailů"])}.`
            : "Služba pro odesílání e-mailů odmítá poštu kvůli chybnému nastavení (klíč, odesílatel nebo adresa služby).",
        note: "Nic neodejde, dokud se nastavení neopraví. Čekající e-maily po týdnu propadnou.",
        cta: { label: "Zobrazit e-maily", to: "/admin/maily" },
      };
    }
    case "teams_pending_approval": {
      const n = num(d, "count");
      return {
        icon: Building2,
        title: "Týmy čekají na schválení",
        body: `Na schválení ${pluralizeCzech(n, ["čeká", "čekají", "čeká"])} ${n} ${teams(n)}.`,
        note: "Neschválený tým se nemůže registrovat do sezóny ani podávat přihlášky.",
        cta: { label: "Zobrazit týmy", to: "/admin/tymy?pohled=ke-schvaleni" },
      };
    }
    case "registration_opens":
      return {
        icon: CalendarClock,
        title: "Registrace se blíží",
        body: `Registrace do sezóny ${str(d, "season_code")} se otevře ${formatDate(str(d, "opens_on"))}.`,
      };
    case "registration_closing": {
      const n = num(d, "days_left");
      return {
        icon: Clock,
        title: "Registrace brzy končí",
        body: `Registrace do sezóny ${str(d, "season_code")} končí ${formatDate(str(d, "closes_on"))}, zbývá ${n} ${days(n)}.`,
        note: "Po uzavření registračního okna už soupisku nelze měnit.",
      };
    }
    case "roster_not_transferred":
      return {
        icon: ClipboardList,
        title: "Soupiska nepřevedena",
        body: `Tým ${str(d, "team_name")}: soupiska ještě nebyla převedena do sezóny ${str(d, "season_code")}.`,
        note: "Bez registrované soupisky se tým nemůže zúčastnit MČR.",
        cta: { label: "Převést soupisku", to: `/teams/${str(d, "team_id")}/registrace` },
      };
    case "roster_empty":
      return {
        icon: ClipboardList,
        title: "Prázdná soupiska",
        body: `Tým ${str(d, "team_name")} nemá v sezóně ${str(d, "season_code")} žádné členy.`,
        cta: { label: "Přidat členy", to: `/teams/${str(d, "team_id")}/registrace` },
      };
    // The rozpracovaná registrace (docs/rozpracovana-registrace.md §10a).
    // Once the window is shut the leader cannot open the page any more, so
    // the closed form says what happens to it instead of offering a link.
    case "season_registration_draft": {
      const n = num(d, "changes");
      const people = `${n} ${pluralizeCzech(n, ["osoby", "osob", "osob"])}`;
      if (d.closed === true) {
        return {
          icon: FilePen,
          title: "Registrace nebyla odeslána",
          body: `Tým ${str(d, "team_name")}: rozpracovaná registrace do sezóny ${str(d, "season_code")} (změny u ${people}) nebyla odeslána a registrační okno je uzavřené.`,
          note: str(d, "gone_on")
            ? `Rozpracovaná registrace se ${formatDate(str(d, "gone_on"))} smaže. Obraťte se na ČNSMS.`
            : "Obraťte se na ČNSMS.",
        };
      }
      return {
        icon: FilePen,
        title: "Rozpracovaná registrace",
        body: `Tým ${str(d, "team_name")}: registrace do sezóny ${str(d, "season_code")} je rozpracovaná (změny u ${people}, uloženo ${formatDate(str(d, "updated_on"))}).`,
        note: str(d, "closes_on")
          ? `Registrační okno se uzavírá ${formatDate(str(d, "closes_on"))}. Do té doby registraci odešlete.`
          : undefined,
        cta: { label: "Pokračovat v registraci", to: `/teams/${str(d, "team_id")}/registrace` },
      };
    }
    case "team_unpaid": {
      const n = num(d, "count");
      return {
        icon: CreditCard,
        title: "Nezaplacené týmy",
        body: `Nezaplacený členský příspěvek ${pluralizeCzech(n, ["má", "mají", "má"])} ${n} ${teams(n)}.`,
        cta: { label: "Zobrazit týmy", to: "/admin/tymy?k-doreseni=nezaplaceno" },
      };
    }
    case "team_not_paid":
      return {
        icon: CreditCard,
        title: "Nezaplacený členský příspěvek",
        body: `Tým ${str(d, "team_name")} nemá zaplacený členský příspěvek pro sezónu ${str(d, "season_code")}.`,
        cta: { label: "Zobrazit tým", to: `/teams/${str(d, "team_id")}` },
      };
    case "members_missing_photo": {
      const n = num(d, "count");
      return {
        icon: Camera,
        title: "Chybějící fotky členů",
        body: `Tým ${str(d, "team_name")}: chybí fotka u ${n} ${members(n)}.`,
        cta: { label: "Doplnit fotky", to: `/teams/${str(d, "team_id")}` },
      };
    }
    case "team_documents_missing": {
      const docs = Array.isArray(d.documents) ? d.documents.map(String) : [];
      return {
        icon: FileSignature,
        title: "Neodsouhlasené dokumenty sezóny",
        body: `Tým ${str(d, "team_name")} zatím neodsouhlasil ${docs.length === 1 ? "dokument" : "dokumenty"}: ${docs.join(", ")}.`,
        cta: { label: "Potvrdit souhlas", to: `/teams/${str(d, "team_id")}` },
      };
    }
    case "teams_documents_missing": {
      const n = num(d, "count");
      return {
        icon: FileSignature,
        title: "Neodsouhlasené dokumenty sezóny",
        body: `Dokumenty sezóny ${str(d, "season_code")} ${pluralizeCzech(n, ["neodsouhlasil", "neodsouhlasily", "neodsouhlasilo"])} ${n} ${pluralizeCzech(n, ["zaregistrovaný tým", "zaregistrované týmy", "zaregistrovaných týmů"])}.`,
        cta: str(d, "season_id")
          ? { label: "Dokumenty sezóny", to: `/admin/sezony/${str(d, "season_id")}/dokumenty` }
          : { label: "Zobrazit týmy", to: "/admin/tymy" },
      };
    }
    // --- Modul 4 + 5: soutěže a přihlášky -------------------------------
    case "competition_deadline_near": {
      const n = num(d, "days_left");
      const entries = num(d, "entry_count");
      return {
        icon: Clock,
        title: "Blíží se uzávěrka přihlášek",
        body: `${str(d, "competition_name")} · uzávěrka ${formatDate(str(d, "deadline"))} ${str(d, "deadline_time")}, ${leftIn(n)}.`,
        note: `Tým ${str(d, "team_name")} má zatím ${entries} ${entriesWord(entries)}. Do uzávěrky lze přidávat i měnit.`,
        cta: {
          label: "Naše přihlášky",
          to: `/teams/${str(d, "team_id")}/souteze/${str(d, "competition_id")}`,
        },
      };
    }
    case "competition_no_entries": {
      const n = num(d, "days_left");
      return {
        icon: FilePen,
        title: "Žádná přihláška na MČR",
        body: `${str(d, "competition_name")} · uzávěrka ${formatDate(str(d, "deadline"))} ${str(d, "deadline_time")}, ${leftIn(n)}.`,
        note: `Tým ${str(d, "team_name")} zatím nikoho nepřihlásil.`,
        cta: {
          label: "Přihlásit",
          to: `/teams/${str(d, "team_id")}/souteze/${str(d, "competition_id")}/nova-prihlaska`,
        },
      };
    }
    case "competition_draft_soon": {
      const n = num(d, "days_left");
      return {
        icon: Trophy,
        title: "Soutěž stále není zveřejněná",
        body: `${str(d, "competition_name")} se koná ${formatDate(str(d, "date"))}, ${leftIn(n)}, a není zveřejněná.`,
        note: "Dokud není zveřejněná, nemůže se na ni nikdo přihlásit.",
        cta: { label: "Nastavení soutěže", to: `/soutez/${str(d, "competition_id")}/nastaveni` },
      };
    }
    case "competition_entries_empty": {
      const n = num(d, "days_left");
      return {
        icon: FilePen,
        title: "Soutěž nemá žádné přihlášky",
        body: `${str(d, "competition_name")} · uzávěrka ${formatDate(str(d, "deadline"))} ${str(d, "deadline_time")}, ${leftIn(n)}.`,
        cta: { label: "Zobrazit přihlášky", to: `/soutez/${str(d, "competition_id")}/prihlasky` },
      };
    }
    case "competition_music_missing": {
      const n = num(d, "count");
      const days = num(d, "days_left");
      return {
        icon: Music,
        title: "Chybí hudba k přihláškám",
        body: `${str(d, "team_name")}: ${n} ${entriesWord(n)} na soutěž ${str(d, "competition_name")} ${pluralizeCzech(n, ["nemá", "nemají", "nemá"])} nahranou hudbu.`,
        note: `Uzávěrka hudby ${formatDate(str(d, "deadline"))} ${str(d, "deadline_time")}, ${leftIn(days)}.`,
        cta: {
          label: "Doplnit hudbu",
          to: `/teams/${str(d, "team_id")}/souteze/${str(d, "competition_id")}`,
        },
      };
    }
    // The taper let clubs withdraw themselves, so the organizer stopped
    // being the one doing it. Info rather than warn: an odhláška is a
    // normal thing that happens and the programme absorbs it. It just
    // must not happen unseen.
    case "competition_withdrawn": {
      const n = num(d, "count");
      return {
        icon: UserMinus,
        title: "Odhlášky na vaší soutěži",
        body: `${str(d, "competition_name")}: ${n} ${pluralizeCzech(n, ["odhlášená přihláška", "odhlášené přihlášky", "odhlášených přihlášek"])}.`,
        note: "Tým může přihlášku odhlásit až do uzávěrky změn soutěžících; zkontrolujte, že startovní listina odpovídá.",
        cta: { label: "Zobrazit odhlášky", to: `/soutez/${str(d, "competition_id")}/prihlasky` },
      };
    }
    case "checkin_handoff": {
      const n = num(d, "count");
      return {
        icon: UserMinus,
        title: "Prezence předala přihlášky",
        body: `${str(d, "competition_name")}: ${n} ${pluralizeCzech(n, ["přihláška čeká", "přihlášky čekají", "přihlášek čeká"])} na změnu soutěžících.`,
        note: "Někdo se nedostavil a soupisku přihlášky mění jen pořadatel. Upozornění zmizí, až změna problém vyřeší.",
        cta: { label: "Zobrazit přihlášky", to: `/soutez/${str(d, "competition_id")}/prihlasky?prezence=1` },
      };
    }
    case "competition_rule_set_not_active": {
      const n = num(d, "count");
      return {
        icon: BookOpen,
        title: "Sada pravidel není aktivní",
        body: `${n} ${pluralizeCzech(n, ["soutěž nejde zveřejnit", "soutěže nejdou zveřejnit", "soutěží nejde zveřejnit"])}, protože jejich sada pravidel není aktivní.`,
        note: "Zveřejnit soutěž lze jen se sadou pravidel ve stavu Aktivní.",
        cta: { label: "Sady pravidel", to: "/admin/pravidla" },
      };
    }
    default:
      return { icon: TriangleAlert, title: "Upozornění", body: "" };
  }
}
